import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { cloneDeep, isEqual } from "lodash";
import { observable, ObservableMap } from "mobx";

import { PermissionBit } from "../api";
import {
  hasAdminAccess,
  hasMedicalAccess,
  hasMonitoringAccess,
} from "../constants/permissions";
import { AccessRules, TableAccess } from "../constants/roles";
import { pinSelectedToTop, assert } from "../utils/utils";
import { EnumFilter, EnumFilterSerialized } from "./data-model/enum-filter";
import { Filter } from "./data-model/filter";
import {
  PersistedFilter,
  PersistedFilterSerialized,
} from "./data-model/persisted-filter";
import { Sort, SortSerialized } from "./data-model/sort";

export const defaultRowsPerPage = 25;

export class DataModel<T extends object = any> {
  @observable accessor name: string | undefined;
  @observable accessor initialized = false;
  @observable accessor paginatedData: T[] = [];
  @observable accessor hasHistory: boolean = false;
  @observable accessor showingHistory: boolean = false;
  idAttr?: keyof T;

  // sorting
  @observable accessor sort: Sort<T> | undefined;

  // filtering
  @observable accessor defaultFilter: Record<string, any[]> | null = null;
  @observable accessor filters: Filter<T>[] = [];

  // editing
  @observable accessor editingIndex: number | undefined;
  // Stable id of the row being edited. Use this in place of `editingIndex`
  // when the rendered rows are sorted/filtered/paginated outside the model —
  // index lookups against `paginatedData` are not reliable in that setup.
  @observable accessor editingItemId: unknown = undefined;
  @observable accessor editingDraft: any;
  // consult `attr in draft` to decide whether they should render in edit mode.
  multiEditDrafts: ObservableMap<unknown, Partial<T>> = observable.map();
  // pagination
  @observable accessor rowsPerPage: number = defaultRowsPerPage;

  // multi-row editing (additive — parallel to the single-row state above).
  // Keyed by item[idAttr] so drafts survive sort/filter/page changes.
  // `multiEditDrafts` is an observable.map so per-entry .set/.delete are reactive.
  // `multiEditInitialDrafts` is a plain Map — only read inside generateMultiPatches,
  // never observed. Each draft's keys are the editable fields for that row — cells
  rowsPerPageOptions: number[] = [10, 25, 50, 100];
  @observable accessor currentPage: number = 1;
  @observable accessor totalPages: number | null = null;
  // access
  @observable accessor canShowHistory: boolean = false;
  @observable accessor access: TableAccess = {
    editRules: { medical: AccessRules.owner, monitoring: AccessRules.visible },
    deleteRules: { medical: AccessRules.none, monitoring: AccessRules.owner },
    historyRequiresMonitoring: true,
  };
  // selection
  @observable accessor selected: any[] = [];
  @observable accessor selectionMode: SelectionMode | undefined;
  @observable accessor data: T[];
  @observable private accessor editingInitialDraft: any;
  @observable private accessor currentlyEditable: (keyof T)[] | undefined;
  private multiEditInitialDrafts = new Map<unknown, Partial<T>>();

  constructor(
    data: T[],
    public permissions?: PermissionBit[],
    public userUUID?: string,
  ) {
    this.data = data;
  }

  private get storageKey() {
    return this.name !== undefined ? `DataModel.${this.name}` : undefined;
  }

  init() {
    this.canShowHistory =
      this.permissions != null &&
      (!this.access.historyRequiresMonitoring ||
        hasMonitoringAccess(this.permissions));

    if (!this.initialized) {
      this.applyDefaultFilter();
    }

    this.computeData();
    this.initialized = true;
  }

  updateData(data: T[]) {
    this.data = [...data];
    this.init();
  }

  setDefaultFilter(filter: Record<string, any[]>) {
    this.defaultFilter = filter;
    this.applyDefaultFilter();
  }

  addFilter(filter: Filter<T>) {
    this.filters.push(filter);
  }

  removeFilter(filter: Filter<T>) {
    const index = this.filters.indexOf(filter);
    if (index !== -1) {
      this.filters.splice(index, 1);
    }
  }

  getOrAddEnumFilter(attr: string) {
    let filter = this.filters.find(
      (f): f is EnumFilter<T> => f instanceof EnumFilter && f.attr === attr,
    );
    if (!filter) {
      filter = new EnumFilter(attr as keyof T);
      this.filters.push(filter);
    }
    return filter;
  }

  getOrAddPersistedFilter<V>(
    key: string,
    predicate: (item: T, selected: V[]) => boolean,
  ) {
    let filter = this.filters.find(
      (f): f is PersistedFilter<T, V> =>
        f instanceof PersistedFilter && f.key === key,
    );
    if (!filter) {
      filter = new PersistedFilter<T, V>(key, [], predicate);
      this.filters.push(filter);
    } else {
      filter.setPredicate(predicate);
    }
    return filter;
  }

  changeRowsPerPage(value: number) {
    this.rowsPerPage = value;
    this.currentPage = 1;
    this.computeData();
  }

  nextPage() {
    if (this.totalPages != null && this.currentPage < this.totalPages) {
      this.setCurrentPage(this.currentPage + 1);
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.setCurrentPage(this.currentPage - 1);
    }
  }

  firstPage() {
    this.goToPage(1);
  }

  lastPage() {
    if (this.totalPages != null) {
      this.goToPage(this.totalPages);
    }
  }

  goToPage(page: number) {
    if (
      this.totalPages != null &&
      page >= 1 &&
      page <= this.totalPages &&
      page !== this.currentPage
    ) {
      this.setCurrentPage(page);
    }
  }

  reload() {
    this.currentPage = 1;
    this.computeData();
  }

  isSelected(id: any) {
    return this.selected.includes(id);
  }

  select(id: any, exclusive: boolean) {
    if (exclusive) {
      this.selected = [id];
    } else if (!this.isSelected(id)) {
      this.selected.push(id);
    }
    if (this.selectionMode) this.computeData();
  }

  deselect(id: any) {
    this.selected = this.selected.filter((i) => i !== id);
    if (this.selectionMode) this.computeData();
  }

  selectAll() {
    const idAttr = this.idAttr;
    assert(idAttr != null, "idAttr must be set to use selection");
    const filtered = this.sortFilterData();
    this.selected = filtered.map((item) => item[idAttr]);
    if (this.selectionMode) this.computeData();
  }

  /**
   * Item-based variant of {@link selectAll} for callers that manage their own
   * visible set — `sortFilterData()` doesn't see externally-applied filters.
   */
  selectAllItems(items: T[]) {
    const idAttr = this.idAttr;
    assert(idAttr != null, "idAttr must be set to use selection");
    this.selected = items.map((item) => item[idAttr]);
    if (this.selectionMode) this.computeData();
  }

  deselectAll() {
    this.selected = [];
    if (this.selectionMode) this.computeData();
  }

  computeData() {
    const sortedFiltered = this.sortFilterData();
    this.paginatedData = this.sliceForCurrentPage(sortedFiltered);
    this.setTotalPages(
      Math.max(Math.ceil(sortedFiltered.length / this.rowsPerPage), 1),
    );
  }

  sortFilterData() {
    let res = this.data;

    // Filter
    res = applyFilters(res, this.filters);
    res = res.filter((item) => this.filterItem(item));

    // Sort
    if (this.sort) {
      res = applySort(res, this.sort);
    }

    // Pin selected items to the top
    if (this.selectionMode && this.selected.length > 0) {
      const idAttr = this.idAttr;
      assert(idAttr != null, "idAttr must be set to use selection");
      res = pinSelectedToTop(res, this.selected, (item) => item[idAttr]);
    }

    return res;
  }

  startAdding(fields: (keyof T)[]) {
    this.currentlyEditable = fields;
    this.editingDraft = {};
    this.editingInitialDraft = {};
    this.editingIndex = -1;
  }

  startEditing(index: number, fields: (keyof T)[]) {
    this.currentlyEditable = fields;
    const draft = this.prepareDraft(this.paginatedData[index]);
    this.editingDraft = cloneDeep(draft);
    this.editingInitialDraft = cloneDeep(draft);
    this.editingIndex = index;
    const idAttr = this.idAttr;
    this.editingItemId =
      idAttr != null ? this.paginatedData[index]?.[idAttr] : undefined;
  }

  /**
   * Item-based variant of {@link startEditing}. Prepares the draft from the
   * given item directly — use when the rendered rows are managed externally
   * and index lookups against `paginatedData` would not be reliable.
   */
  startEditingItem(item: T, fields: (keyof T)[]) {
    this.currentlyEditable = fields;
    const draft = this.prepareDraft(item);
    this.editingDraft = cloneDeep(draft);
    this.editingInitialDraft = cloneDeep(draft);
    this.editingIndex = undefined;
    const idAttr = this.idAttr;
    this.editingItemId = idAttr != null ? item[idAttr] : undefined;
  }

  stopEditing() {
    this.editingIndex = undefined;
    this.editingItemId = undefined;
    this.editingDraft = undefined;
    this.editingInitialDraft = undefined;
    this.currentlyEditable = undefined;
  }

  prepareDraft(item: T): Record<string, any> {
    return Object.keys(item)
      .filter((key) => this.currentlyEditable?.includes(key as keyof T))
      .reduce<Record<string, any>>((obj, key) => {
        obj[key] = item[key as keyof T];
        return obj;
      }, {});
  }

  isMultiEditing(id: unknown) {
    return this.multiEditDrafts.has(id);
  }

  startMultiEditing(item: T, fields: (keyof T)[]) {
    const idAttr = this.idAttr;
    assert(idAttr != null, "idAttr must be set to use multi-editing");
    const id = item[idAttr];
    if (this.multiEditDrafts.has(id)) return;
    const draft: Partial<T> = {};
    for (const key of fields) {
      draft[key] = item[key];
    }
    this.multiEditDrafts.set(id, cloneDeep(draft));
    this.multiEditInitialDrafts.set(id, cloneDeep(draft));
  }

  stopMultiEditing(id: unknown) {
    this.multiEditDrafts.delete(id);
    this.multiEditInitialDrafts.delete(id);
  }

  stopAllMultiEditing() {
    this.multiEditDrafts.clear();
    this.multiEditInitialDrafts.clear();
  }

  /** One patch per edited row; each patch contains only changed fields (plus alwaysIncludeKeys). */
  generateMultiPatches(alwaysIncludeKeys?: string[]): Array<{
    id: number;
    patch: Partial<T>;
  }> {
    const out: Array<{ id: number; patch: Partial<T> }> = [];
    for (const [id, draft] of this.multiEditDrafts) {
      const initial: Partial<T> = this.multiEditInitialDrafts.get(id) ?? {};
      const patch: Partial<T> = {};
      for (const key in draft) {
        const k = key as keyof T;
        if (
          alwaysIncludeKeys?.includes(key) ||
          !isEqual(draft[k], initial[k])
        ) {
          patch[k] = draft[k];
        }
      }
      if (Object.keys(patch).length > 0) {
        out.push({ id: id as number, patch });
      }
    }
    return out;
  }

  /** Patch contains only changed fields. */
  generatePatch(alwaysIncludeKeys?: string[]) {
    if (!this.editingDraft) {
      return;
    }

    const patch: any = {};

    for (const key in this.editingDraft) {
      const keyToPatch =
        alwaysIncludeKeys?.includes(key) ||
        !isEqual(this.editingDraft[key], this.editingInitialDraft[key]);

      if (keyToPatch) {
        patch[key] = this.editingDraft[key];
      }
    }

    return patch;
  }

  canEditField(fieldName: keyof T) {
    return (
      !this.currentlyEditable || this.currentlyEditable.includes(fieldName)
    );
  }

  validateItemDelete(item: T): string | undefined {
    if (
      !this.canDelete() ||
      (this.getDeleteRule() === AccessRules.owner &&
        (item as Record<string, unknown>)["user_uuid"] !== this.userUUID)
    ) {
      return i18n._(msg`Not authorized`);
    }

    return undefined;
  }

  validateItemEdit(item: T): string | undefined {
    if (
      !this.canEdit() ||
      (this.getEditRule() === AccessRules.owner &&
        (item as Record<string, unknown>)["user_uuid"] !== this.userUUID)
    ) {
      return i18n._(msg`Not authorized`);
    }

    return undefined;
  }

  canDelete() {
    return this.getDeleteRule() !== AccessRules.none;
  }

  canEdit() {
    return this.getEditRule() !== AccessRules.none;
  }

  toggleHistory() {
    if (this.hasHistory && this.canShowHistory) {
      this.showingHistory = !this.showingHistory;
    }
  }

  saveSettings() {
    if (!this.storageKey) {
      return;
    }

    try {
      const serialized = JSON.stringify(this.serializeSettings());
      sessionStorage.setItem(this.storageKey, serialized);
    } catch (error) {
      console.warn("Cannot persist settings", error);
    }
  }

  loadSettings() {
    if (!this.storageKey) {
      return;
    }

    try {
      const str = sessionStorage.getItem(this.storageKey);
      if (str) {
        const serialized = JSON.parse(str) as SettingsSerialized;
        this.deserializeSettings(serialized);
        this.reload();
      }
    } catch (error) {
      console.warn("Cannot persist settings", error);
    }
  }

  protected setCurrentPage(page: number) {
    this.currentPage = page;
    this.computeData();
  }

  protected setTotalPages(pages: number) {
    this.totalPages = pages;
  }

  protected filterItem(item: T) {
    // Children can implement their custom filtering
    return true;
  }

  protected sliceForCurrentPage<T>(data: T[]): T[] {
    const start = (this.currentPage - 1) * this.rowsPerPage;
    const end = start + this.rowsPerPage;
    return data.slice(start, end);
  }

  private applyDefaultFilter() {
    if (this.defaultFilter != null) {
      for (const attr in this.defaultFilter) {
        this.getOrAddEnumFilter(attr).selected = this.defaultFilter[attr];
      }
    }
  }

  private getDeleteRule() {
    if (!this.permissions) return AccessRules.visible;
    if (hasAdminAccess(this.permissions)) return AccessRules.visible;
    if (hasMonitoringAccess(this.permissions))
      return this.access.deleteRules.monitoring;
    if (hasMedicalAccess(this.permissions))
      return this.access.deleteRules.medical;
    return AccessRules.none;
  }

  private getEditRule() {
    if (!this.permissions) return AccessRules.visible;
    if (hasAdminAccess(this.permissions)) return AccessRules.visible;
    if (hasMonitoringAccess(this.permissions))
      return this.access.editRules.monitoring;
    if (hasMedicalAccess(this.permissions))
      return this.access.editRules.medical;
    return AccessRules.none;
  }

  private serializeSettings(): SettingsSerialized {
    const filters = this.filters.reduce<SerializedFilter[]>((acc, f) => {
      if (f instanceof EnumFilter) {
        acc.push(f.serialize());
      } else if (f instanceof PersistedFilter) {
        acc.push(f.serialize());
      }

      return acc;
    }, []);

    return {
      filters,
      sort: this.sort?.serialize(),
    };
  }

  private deserializeSettings(serialized: SettingsSerialized) {
    // Preserve existing transient filters (anything not serialized below)
    const transientFilters = this.filters.filter(
      (f) => !(f instanceof EnumFilter) && !(f instanceof PersistedFilter),
    );

    const deserializedFilters = serialized.filters.reduce<Filter<T>[]>(
      (acc, f) => {
        if (f.type === "EnumFilter") {
          acc.push(EnumFilter.deserialize(f));
        } else if (f.type === "PersistedFilter") {
          acc.push(PersistedFilter.deserialize<T, any>(f));
        }

        return acc;
      },
      [],
    );

    this.filters = [...transientFilters, ...deserializedFilters];

    if (serialized.sort) {
      this.sort = Sort.deserialize(serialized.sort);
    }
  }
}

export function applySort<T>(data: T[], sort: Sort<T>): T[] {
  return [...data].sort((a, b) => sort.compare(a, b));
}

export function applyFilters<T>(data: T[], filters: Filter<T>[]): T[] {
  return data.filter((item) =>
    Array.from(filters.values()).every((f) => f.filter(item)),
  );
}

interface SettingsSerialized {
  filters: SerializedFilter[];
  sort?: SortSerialized<any>;
}

type SerializedFilter =
  | EnumFilterSerialized<any>
  | PersistedFilterSerialized<any>;

export type SelectionMode = "single" | "multi";
